import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import SvgIcon from "../../../../../parts/SvgIcon";
import {
  parseUnitToShow,
  reCalculateMacros,
} from "../../../../../utils/nutrition-util";
import { truncateText } from "../../../../../utils/string-helpers";

export const FoodItems = ({ items, onRemoveItem, onUpdateItems }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const inputRefs = useRef({});

  /**
   * Focus the input clicking the span
   */
  useEffect(() => {
    if (editingIndex !== null && inputRefs.current[editingIndex]) {
      inputRefs.current[editingIndex].focus();
    }
  }, [editingIndex]);

  /**
   * Not use input + span, just use span
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        editingIndex !== null &&
        inputRefs.current[editingIndex] &&
        !inputRefs.current[editingIndex].contains(event.target)
      ) {
        setEditingIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editingIndex]);

  /**
   * Clean the not used refs
   */
  useEffect(() => {
    return () => {
      inputRefs.current = {};
    };
  }, []);

  /**
   * Function to increase or decrease de quantity number
   * @param {*} index
   * @param {*} newQuantity
   */
  const updateQuantity = (index, newQuantity) => {
    const updatedItem = {
      customQuantityNumber: newQuantity
        ? Math.max(0.1, Math.round(Number(newQuantity) * 100) / 100)
        : "",
    };
    onUpdateItems(index, updatedItem);
  };

  /**
   * Avoid strings in number input
   * @param {*} e
   */
  const handleKeyDown = (e) => {
    if (["e", "E", "-", "+", ","].includes(e.key)) {
      e.preventDefault();
    }
  };

  return items.map((item, index) => {
    const {
      _id,
      customQuantityNumber = 1,
      quantityNumber = 1,
      quantityDescription = "Servings",
      name,
      proteins: itemProteins,
      carbs: itemCarbs,
      fats: itemFats,
      calories: itemCalories,
      unitOfMeasure = "g",
      customUnitOfMeasure = "custom",
      measurementsValue,
      type,
      skipRecalculation,
    } = item;
    const itemDetails = {
      itemProteins,
      itemCarbs,
      itemFats,
      itemCalories,
      foodKey: "foodId",
      customQuantityNumber,
      quantityNumber,
      customUnitOfMeasure,
      unitOfMeasure,
      measurementsValue,
      type,
      skipRecalculation,
    };

    const { proteins, carbs, fats, calories } = reCalculateMacros({
      itemDetails,
    });
    return (
      <div key={`${_id}-${index}`} className={`content`}>
        <div className="info-food">
          <div>
            <span>{name}</span>
          </div>
          <div className="nutrition-info">
            <span>Protein {proteins}g </span>
            <span>&#8226;</span>
            <span>Carbs {carbs}g</span>
            <span>&#8226;</span>
            <span>Fat {fats}g</span>
            <span>&#8226;</span>
            <span>Calories {calories}</span>
          </div>
        </div>
        <div className="actions">
          <div className="number-container">
            <button
              onClick={() =>
                updateQuantity(index, Number(customQuantityNumber) - 1)
              }
            >
              <SvgIcon src="/img/svg/angle-down.svg" />
            </button>
            {editingIndex === index ? (
              <div className="flex items-center">
                <input
                  ref={(el) => (inputRefs.current[index] = el)}
                  min={0.1}
                  type="number"
                  value={customQuantityNumber || ""}
                  onKeyDown={(e) => {
                    handleKeyDown(e);
                    if (e.key === "Enter") setEditingIndex(null);
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    //Allow only 2 decimals
                    if (/^\d*\.?\d{0,2}$/.test(value)) {
                      updateQuantity(index, value);
                    }
                  }}
                />
                <span className="span-edit">
                  {truncateText(
                    parseUnitToShow(
                      customQuantityNumber,
                      type,
                      customUnitOfMeasure,
                      quantityDescription
                    ),
                    8
                  )}
                </span>
              </div>
            ) : (
              <span
                className="span-view"
                onClick={() => setEditingIndex(index)}
              >
                {truncateText(
                  `${customQuantityNumber} ${parseUnitToShow(
                    customQuantityNumber,
                    type,
                    customUnitOfMeasure,
                    quantityDescription
                  )}`,
                  12
                )}
              </span>
            )}
            <button
              onClick={() =>
                updateQuantity(index, Number(customQuantityNumber) + 1)
              }
            >
              <SvgIcon src="/img/svg/angle-up.svg" />
            </button>
          </div>
          <button onClick={() => onRemoveItem(index)}>
            <img src="/img/new-icons/red-cross.svg" />
          </button>
        </div>
      </div>
    );
  });
};

FoodItems.propTypes = {
  items: PropTypes.array,
  handleRemoveItem: PropTypes.func,
  onUpdateItems: PropTypes.func,
};
